import React, { useState, useEffect } from 'react';
import { FileText, Search, DollarSign, Eye } from 'lucide-react';
import { solicitudesService } from '../../../../services/api';
import OrdenTrabajoDetailModal from './OrdenTrabajoDetailModal';

const ESTADOS_CONFIG = {
  'solicitud_nueva': { label: 'Solicitud Nueva', color: '#3B82F6' },
  'pedido_en_espera': { label: 'Pedido en Espera', color: '#8B5CF6' },
  'anticipo_recibido': { label: 'Anticipo Recibido', color: '#06B6D4' },
  'pendiente_diferencial': { label: 'Pendiente por Diferencial', color: '#EC4899' },
  'listo_para_facturar': { label: 'Listo para Facturar', color: '#14B8A6' },
  'en_proceso': { label: 'En Proceso', color: '#F59E0B' },
  'completada': { label: 'Completada', color: '#10B981' }
};

const OrdenesTrabajoList = () => {
  const [ordenes, setOrdenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [filtroAnticipo, setFiltroAnticipo] = useState('todos');
  const [selectedOrden, setSelectedOrden] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    cargarOrdenes();
  }, []);

  const cargarOrdenes = async () => {
    try {
      setLoading(true);
      const res = await solicitudesService.getAll({});
      const ordenesTrabajo = (res.data || []).filter(s => s.vehiculo_id != null);
      setOrdenes(ordenesTrabajo);
    } catch (error) {
      console.error('Error cargando órdenes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (orden) => {
    setSelectedOrden(orden);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setSelectedOrden(null);
    setShowModal(false);
  };

  const handleOrdenUpdated = () => {
    cargarOrdenes();
  };

  const ordenesFiltradas = ordenes.filter(orden => {
    const matchSearch = orden.nombre_solicitud?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       orden.descripcion?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchEstado = filtroEstado === 'todos' || orden.estado === filtroEstado;
    const matchAnticipo = filtroAnticipo === 'todos' ||
                         (filtroAnticipo === 'con_anticipo' && orden.requiere_anticipo) ||
                         (filtroAnticipo === 'sin_anticipo' && !orden.requiere_anticipo);
    return matchSearch && matchEstado && matchAnticipo;
  });

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-bold text-blue-800 flex items-center gap-2">
          <FileText size={20} />
          Órdenes de Trabajo
        </h2>
        <p className="text-sm text-gray-600">Gestión de órdenes de trabajo creadas desde Multi-Servicios</p>
      </div>

      <div className="p-4 bg-gray-50 border-b border-gray-200">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar órdenes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
          >
            <option value="todos">Todos los Estados</option>
            {Object.entries(ESTADOS_CONFIG).map(([key, config]) => (
              <option key={key} value={key}>{config.label}</option>
            ))}
          </select>
          <select
            value={filtroAnticipo}
            onChange={(e) => setFiltroAnticipo(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
          >
            <option value="todos">Todos (Anticipo)</option>
            <option value="con_anticipo">Con Anticipo</option>
            <option value="sin_anticipo">Sin Anticipo</option>
          </select>
        </div>
      </div>

      <div className="p-6">
        {loading ? (
          <div className="text-center py-8">
            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : ordenesFiltradas.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FileText size={40} className="mx-auto mb-2 text-gray-300" />
            <p>No se encontraron órdenes de trabajo</p>
          </div>
        ) : (
          <div className="space-y-4">
            {ordenesFiltradas.map(orden => (
              <div
                key={orden.id}
                className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50/50 transition-all cursor-pointer shadow-sm"
                onClick={() => handleOpenModal(orden)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-gray-800">{orden.nombre_solicitud}</h3>
                      <span
                        className="px-2 py-1 rounded text-xs font-semibold text-white"
                        style={{ backgroundColor: ESTADOS_CONFIG[orden.estado]?.color || '#6B7280' }}
                      >
                        {ESTADOS_CONFIG[orden.estado]?.label || orden.estado}
                      </span>
                      {orden.requiere_anticipo && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-semibold border border-purple-300">
                          <DollarSign size={10} />
                          Con Anticipo
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-2 line-clamp-2">{orden.descripcion}</p>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>Folio: {orden.folio || `#${orden.id}`}</span>
                      {orden.requiere_anticipo && (
                        <span className="text-purple-600">
                          Anticipo: ${Number(orden.anticipo_monto_usd).toFixed(2)} (Tasa: {orden.anticipo_tasa_cambio})
                        </span>
                      )}
                    </div>
                  </div>
                  <Eye size={18} className="text-gray-400" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && selectedOrden && (
        <OrdenTrabajoDetailModal
          orden={selectedOrden}
          onClose={handleCloseModal}
          onUpdated={handleOrdenUpdated}
        />
      )}
    </div>
  );
};

export default OrdenesTrabajoList;
